import { getTransportById, getTransportGpsSignals } from "@/features/transports/api";
import { getWaypointEvents, getTransportLog } from "@/features/transportDetails/api";
import { GPS_SIGNALS_MODES, isTransportCancelled } from "@/core/transport";

export const EVENT = {
  RESET_REQUEST_IN_PROGRESS_TRIGGERED: "RESET_REQUEST_IN_PROGRESS_TRIGGERED",
  RESET_TRIGGERED: "RESET_TRIGGERED",
  REQUEST_GPS_SIGNALS_FAILED: "REQUEST_GPS_SIGNALS_FAILED",
  REQUEST_TRANSPORT_DETAILS_FAILED: "REQUEST_TRANSPORT_DETAILS_FAILED",
  REQUEST_WAYPOINT_EVENTS_FAILED: "REQUEST_WAYPOINT_EVENTS_FAILED",
  REQUEST_TRANSPORT_LOG_FAILED: "REQUEST_TRANSPORT_LOG_FAILED",
  REQUEST_GPS_SIGNALS_STARTED: "REQUEST_GPS_SIGNALS_STARTED",
  REQUEST_GPS_SIGNALS_SUCCEEDED: "REQUEST_GPS_SIGNALS_SUCCEEDED",
  REQUEST_TRANSPORT_DETAILS_SUCCEEDED: "REQUEST_TRANSPORT_DETAILS_SUCCEEDED",
  REQUEST_WAYPOINT_EVENTS_SUCCEEDED: "REQUEST_WAYPOINT_EVENTS_SUCCEEDED",
  REQUEST_TRANSPORT_LOG_SUCCEEDED: "REQUEST_TRANSPORT_LOG_SUCCEEDED",
  REQUESTS_STARTED: "REQUESTS_STARTED",
  UPDATE_FIRST_LOAD_TRIGGERED: "UPDATE_FIRST_LOAD_TRIGGERED",
  UPDATE_GPS_SIGNALS_MODE_TRIGGERED: "UPDATE_GPS_SIGNALS_MODE_TRIGGERED",
};

export const defaultState = () => ({
  gpsSignals: [],
  gpsSignalsError: null,
  gpsSignalsMode: GPS_SIGNALS_MODES.TRANSIT,
  isFirstLoad: true,
  isRequestInProgress: false,
  transportDetails: {},
  transportDetailsError: null,
  waypointEventsError: null,
  waypointsEvents: {},
  transportLogError: null,
  transportLog: [],
});

export const mutations = {
  [EVENT.RESET_REQUEST_IN_PROGRESS_TRIGGERED](state) {
    state.isRequestInProgress = false;
  },

  [EVENT.RESET_TRIGGERED](state) {
    Object.assign(state, defaultState());
  },

  [EVENT.REQUEST_GPS_SIGNALS_FAILED](state, error) {
    state.gpsSignals = state.gpsSignals ?? [];
    state.gpsSignalsError = error;
  },

  [EVENT.REQUEST_TRANSPORT_DETAILS_FAILED](state, error) {
    state.transportDetailsError = error;
  },

  [EVENT.REQUEST_WAYPOINT_EVENTS_FAILED](state, error) {
    state.waypointEventsError = error;
  },

  [EVENT.REQUEST_TRANSPORT_LOG_FAILED](state, error) {
    state.transportLogError = error;
  },

  [EVENT.REQUEST_GPS_SIGNALS_STARTED](state) {
    state.gpsSignals = null;
  },

  [EVENT.REQUEST_GPS_SIGNALS_SUCCEEDED](state, response) {
    state.gpsSignals = response;
    state.gpsSignalsError = null;
  },

  [EVENT.REQUEST_TRANSPORT_DETAILS_SUCCEEDED](state, response) {
    state.transportDetails = response;
    state.transportDetailsError = null;
  },

  [EVENT.REQUEST_WAYPOINT_EVENTS_SUCCEEDED](state, { events, waypointId }) {
    state.waypointsEvents = {
      ...state.waypointsEvents,
      [waypointId]: events,
    };
    state.waypointEventsError = null;
  },

  [EVENT.REQUEST_TRANSPORT_LOG_SUCCEEDED](state, response) {
    state.transportLog = response;
    state.transportLogError = null;
  },

  [EVENT.REQUESTS_STARTED](state) {
    state.isRequestInProgress = true;
  },

  [EVENT.UPDATE_FIRST_LOAD_TRIGGERED](state) {
    state.isFirstLoad = false;
  },

  [EVENT.UPDATE_GPS_SIGNALS_MODE_TRIGGERED](state, mode) {
    state.gpsSignalsMode = mode;
  },
};

export const actions = ({
  getTransportById,
  getTransportGpsSignals,
  getWaypointEvents,
  getTransportLog,
}) => ({
  async getGpsSignals({ commit, state }, transportId) {
    const mode = state.gpsSignalsMode;

    try {
      const gpsSignals = await getTransportGpsSignals(transportId, mode);
      // Ignore the response if the mode was switched while it was loading
      if (mode === state.gpsSignalsMode) {
        commit(EVENT.REQUEST_GPS_SIGNALS_SUCCEEDED, gpsSignals);
      }
    } catch (error) {
      commit(EVENT.REQUEST_GPS_SIGNALS_FAILED, error);
    }
  },

  async setGpsSignalsMode({ commit, dispatch, state }, mode) {
    if (mode === state.gpsSignalsMode) return;

    commit(EVENT.UPDATE_GPS_SIGNALS_MODE_TRIGGERED, mode);

    const transportId = state.transportDetails?.id;
    if (transportId) {
      commit(EVENT.REQUEST_GPS_SIGNALS_STARTED);
      await dispatch("getGpsSignals", transportId);
    }
  },

  async getTransportDetails({ commit, dispatch }, transportId) {
    commit(EVENT.REQUESTS_STARTED);

    let transportDetails = null;

    try {
      transportDetails = await getTransportById({ transportId, includeCancelled: true });
      commit(EVENT.REQUEST_TRANSPORT_DETAILS_SUCCEEDED, transportDetails);
    } catch (error) {
      commit(EVENT.REQUEST_TRANSPORT_DETAILS_FAILED, error);
      commit(EVENT.RESET_REQUEST_IN_PROGRESS_TRIGGERED);
      return;
    }

    await dispatch("getLog", transportId);

    if (!isTransportCancelled(transportDetails)) {
      const transportId = transportDetails.id;
      const { waypoints } = transportDetails;
      await dispatch("getGpsSignals", transportId);

      if (waypoints) {
        await Promise.all(
          waypoints.map((waypoint) => dispatch("getWaypointEvents", { transportId, waypointId: waypoint.id }))
        );
      }
    }

    commit(EVENT.UPDATE_FIRST_LOAD_TRIGGERED);
    commit(EVENT.RESET_REQUEST_IN_PROGRESS_TRIGGERED);
  },

  async getWaypointEvents({ commit }, { transportId, waypointId }) {
    try {
      const events = await getWaypointEvents({ transportId, waypointId });
      commit(EVENT.REQUEST_WAYPOINT_EVENTS_SUCCEEDED, {
        events,
        waypointId,
      });
    } catch (error) {
      commit(EVENT.REQUEST_WAYPOINT_EVENTS_FAILED, error);
    }
  },

  async getLog({ commit }, transportId) {
    try {
      const transportLog = await getTransportLog(transportId);
      commit(EVENT.REQUEST_TRANSPORT_LOG_SUCCEEDED, transportLog);
    } catch (error) {
      commit(EVENT.REQUEST_TRANSPORT_LOG_FAILED, error);
    }
  },

  async refresh({ dispatch, state }) {
    const { transportDetails } = state;

    if (transportDetails && transportDetails.id) {
      dispatch("getTransportDetails", transportDetails.id);
    }
  },

  reset({ commit }) {
    commit(EVENT.RESET_TRIGGERED);
  },
});

/**
 * Builds a store module for transport details.
 * @returns {Object} - A Vuex module.
 */
export function buildTransportDetailsModule() {
  return {
    namespaced: true,

    state: defaultState(),

    mutations,

    actions: actions({
      getTransportById,
      getTransportGpsSignals,
      getWaypointEvents,
      getTransportLog,
    }),
  };
}
